import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Clock, X } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SubscriptionSuccess = () => {
  const [status, setStatus] = useState("checking");
  const { checkAuth, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkPayment = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("session_id");
      const paymentIntent = params.get("payment_intent");

      // If coming from Payment Element redirect, use payment_intent to find subscription
      if (paymentIntent && !sessionId) {
        try {
          // Poll the backend to check if the subscription was activated via webhook
          let attempts = 0;
          const maxAttempts = 15;
          const poll = async () => {
            try {
              const res = await fetch(`${API}/payments/verify-payment-intent/${paymentIntent}`, { credentials: "include" });
              const data = await res.json();
              if (data.status === "active" || data.status === "trialing") {
                if (data.user) {
                  login(data.user);
                  setStatus("success_new_user");
                } else {
                  setStatus("success");
                  await checkAuth();
                }
                setTimeout(() => navigate("/dashboard"), 1500);
              } else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(poll, 2000);
              } else {
                setStatus("pending");
              }
            } catch {
              if (attempts < maxAttempts) { attempts++; setTimeout(poll, 2000); }
              else { setStatus("pending"); }
            }
          };
          poll();
        } catch { setStatus("error"); }
        return;
      }

      if (!sessionId) { setStatus("error"); return; }

      let attempts = 0;
      const maxAttempts = 10;

      const poll = async () => {
        try {
          const response = await fetch(`${API}/payments/checkout-status/${sessionId}`, { credentials: "include" });
          const data = await response.json();
          if (data.payment_status === "paid") {
            if (data.user) {
              login(data.user);
              setStatus(data.is_new_user ? "success_new_user" : "success");
              setTimeout(() => navigate("/dashboard"), 1500);
            } else {
              setStatus("success");
              await checkAuth();
              setTimeout(() => navigate("/dashboard"), 1500);
            }
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 2000);
          } else {
            setStatus("pending");
          }
        } catch (error) {
          if (attempts < maxAttempts) { attempts++; setTimeout(poll, 2000); }
          else { setStatus("error"); }
        }
      };
      poll();
    };
    checkPayment();
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        {status === "checking" && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mx-auto mb-6"></div>
            <h2 className="font-heading text-2xl font-bold mb-2">Setting Up Your Account</h2>
            <p className="text-text-secondary">Just a moment while we activate your membership...</p>
          </>
        )}
        {status === "success_new_user" && (
          <>
            <div className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto mb-6"><Check className="w-10 h-10 text-white" /></div>
            <h2 className="font-heading text-3xl font-bold mb-3">You're All Set!</h2>
            <p className="text-text-secondary mb-2">Welcome to Paladin! Your membership is now active.</p>
            <p className="text-primary font-semibold mb-4">Time to start your fitness journey!</p>
            <button onClick={() => navigate("/dashboard")} className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-600 transition-colors">Go to Dashboard</button>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto mb-6"><Check className="w-10 h-10 text-white" /></div>
            <h2 className="font-heading text-3xl font-bold mb-3">Payment Successful!</h2>
            <p className="text-text-secondary mb-4">Your subscription has been activated.</p>
            <button onClick={() => navigate("/dashboard")} className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-600 transition-colors">Start Working Out</button>
          </>
        )}
        {status === "pending" && (
          <>
            <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-6"><Clock className="w-8 h-8 text-white" /></div>
            <h2 className="font-heading text-2xl font-bold mb-2">Almost There!</h2>
            <p className="text-text-secondary mb-4">Your payment is being processed. You can proceed to your dashboard.</p>
            <button onClick={() => navigate("/dashboard")} className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-600 transition-colors">Go to Dashboard</button>
          </>
        )}
        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-destructive rounded-full flex items-center justify-center mx-auto mb-6"><X className="w-8 h-8 text-white" /></div>
            <h2 className="font-heading text-2xl font-bold mb-2">Payment Issue</h2>
            <p className="text-text-secondary mb-4">Something went wrong with your payment. Please try again.</p>
            <button onClick={() => navigate("/pricing")} className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-600 transition-colors">Try Again</button>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
